import './bufferPolyfill'
import { Buffer } from 'buffer'
import * as ed25519 from '@noble/ed25519'
import {
  Cell,
  type MessageRelaxed,
  SendMode,
  beginCell,
  external,
  internal,
  storeMessage,
  storeStateInit,
} from '@ton/core'
import { WalletContractV5R1 } from '@ton/ton'

const WALLET_VERSION = 'v5r1' as const
const MAINNET_GLOBAL_ID = -239
const TESTNET_GLOBAL_ID = -3
const DEFAULT_WORKCHAIN = 0
const DEFAULT_SUBWALLET_NUMBER = 0
const DEFAULT_SEND_MODE = SendMode.PAY_GAS_SEPARATELY | SendMode.IGNORE_ERRORS
const MAX_V5R1_OUT_MESSAGES = 255
const DEFAULT_MIN_VALID_UNTIL_SECONDS = 20

export type TonNetwork = 'mainnet' | 'testnet'

export type TonV5R1Address = {
  friendly: string
  raw: string
  walletVersion: typeof WALLET_VERSION
  networkGlobalId: number
  workchain: number
  subwalletNumber: number
}

export type DeriveTonV5R1AddressOptions = {
  publicKey: Uint8Array
  network?: TonNetwork
}

export type BuildSignedTonV5R1TransferOptions = DeriveTonV5R1AddressOptions & {
  signingKeySeed: Uint8Array
  to: string
  amountNano: bigint
  seqno: number
  validUntil: number
  sendMode?: number
  bounce?: boolean
  bodyBocBase64?: string
  includeStateInit?: boolean
  nowUnix?: number
  minValidUntilSeconds?: number
}

export type TonV5R1InternalMessagePlan = {
  to: string
  amountNano: bigint
  bodyBocBase64?: string
  bounce?: boolean
}

export type BuildSignedTonV5R1BatchOptions = DeriveTonV5R1AddressOptions & {
  signingKeySeed: Uint8Array
  messages: TonV5R1InternalMessagePlan[]
  seqno: number
  validUntil: number
  sendMode?: number
  includeStateInit?: boolean
  nowUnix?: number
  minValidUntilSeconds?: number
}

export type SignedTonV5R1Transfer = {
  walletAddress: TonV5R1Address
  walletVersion: typeof WALLET_VERSION
  networkGlobalId: number
  seqno: number
  validUntil: number
  sendMode: number
  signingHashHex: string
  signatureHex: string
  signatureVerified: boolean
  signedBodyBocBase64: string
  signedBodyHashHex: string
  externalMessageBocBase64: string
  externalMessageHashHex: string
  /** Base64 of the external message hash — the encoding the relay's tx_hash
      and the wallet indexer's in_msg_hash both use. */
  externalMessageHashB64: string
  includesStateInit: boolean
  messageCount: number
}

export async function getEd25519PublicKey(seed: Uint8Array): Promise<Uint8Array> {
  assertByteLength(seed, 32, 'ed25519 seed')
  return new Uint8Array(await ed25519.getPublicKeyAsync(copyBytes(seed)))
}

export async function signEd25519Rfc8032(
  message: Uint8Array,
  seed: Uint8Array
): Promise<Uint8Array> {
  assertByteLength(seed, 32, 'ed25519 seed')
  return new Uint8Array(await ed25519.signAsync(copyBytes(message), copyBytes(seed)))
}

export async function verifyEd25519Signature(
  signature: Uint8Array,
  message: Uint8Array,
  publicKey: Uint8Array
): Promise<boolean> {
  assertByteLength(signature, 64, 'ed25519 signature')
  assertByteLength(publicKey, 32, 'ed25519 public key')
  return ed25519.verifyAsync(copyBytes(signature), copyBytes(message), copyBytes(publicKey), {
    zip215: false,
  })
}

export function deriveTonV5R1Address(options: DeriveTonV5R1AddressOptions): TonV5R1Address {
  const network = options.network ?? 'mainnet'
  const wallet = createTonV5R1Wallet(options.publicKey, network)

  return {
    friendly: wallet.address.toString({
      bounceable: true,
      testOnly: network === 'testnet',
      urlSafe: true,
    }),
    raw: wallet.address.toRawString(),
    walletVersion: WALLET_VERSION,
    networkGlobalId: networkGlobalId(network),
    workchain: DEFAULT_WORKCHAIN,
    subwalletNumber: DEFAULT_SUBWALLET_NUMBER,
  }
}

/** Public deployment material for cold-wallet emulation and the first external. */
export function buildTonV5R1StateInitBocBase64(options: DeriveTonV5R1AddressOptions): string {
  const network = options.network ?? 'mainnet'
  const wallet = createTonV5R1Wallet(options.publicKey, network)
  return cellToBase64(beginCell().store(storeStateInit(wallet.init)).endCell())
}

export async function buildSignedTonV5R1Transfer(
  options: BuildSignedTonV5R1TransferOptions
): Promise<SignedTonV5R1Transfer> {
  return buildSignedTonV5R1BatchTransfer({
    ...options,
    messages: [
      {
        to: options.to,
        amountNano: options.amountNano,
        bodyBocBase64: options.bodyBocBase64,
        bounce: options.bounce,
      },
    ],
  })
}

export async function buildSignedTonV5R1BatchTransfer(
  options: BuildSignedTonV5R1BatchOptions
): Promise<SignedTonV5R1Transfer> {
  assertInteger(options.seqno, 'seqno')
  assertInteger(options.validUntil, 'validUntil')
  assertValidUntilFresh(options.validUntil, options.nowUnix, options.minValidUntilSeconds)
  assertMessages(options.messages)

  const publicKey = copyBytes(options.publicKey)
  const seed = copyBytes(options.signingKeySeed)
  assertByteLength(publicKey, 32, 'ed25519 public key')
  assertByteLength(seed, 32, 'ed25519 seed')

  const derivedPublicKey = await getEd25519PublicKey(seed)
  if (!bytesEqual(publicKey, derivedPublicKey)) {
    throw new Error('signing seed does not match public key')
  }

  const network = options.network ?? 'mainnet'
  const wallet = createTonV5R1Wallet(publicKey, network)
  const sendMode = options.sendMode ?? DEFAULT_SEND_MODE
  const messages = options.messages.map(toRelaxedMessage)
  let signingHash: Uint8Array = new Uint8Array()
  let signature: Uint8Array = new Uint8Array()

  const signedBody = await wallet.createTransfer({
    seqno: options.seqno,
    timeout: options.validUntil,
    sendMode,
    signer: async (signingCell: Cell) => {
      signingHash = new Uint8Array(signingCell.hash())
      signature = await signEd25519Rfc8032(signingHash, seed)
      return toBuffer(signature)
    },
    messages,
  })

  const signatureVerified = await verifyEd25519Signature(signature, signingHash, publicKey)
  const includeStateInit = options.includeStateInit ?? false
  const externalMessage = beginCell()
    .store(
      storeMessage(
        external({
          to: wallet.address,
          init: includeStateInit ? wallet.init : undefined,
          body: signedBody,
        })
      )
    )
    .endCell()

  return {
    walletAddress: deriveTonV5R1Address({ publicKey, network }),
    walletVersion: WALLET_VERSION,
    networkGlobalId: networkGlobalId(network),
    seqno: options.seqno,
    validUntil: options.validUntil,
    sendMode,
    signingHashHex: bytesToHex(signingHash),
    signatureHex: bytesToHex(signature),
    signatureVerified,
    signedBodyBocBase64: cellToBase64(signedBody),
    signedBodyHashHex: bytesToHex(signedBody.hash()),
    externalMessageBocBase64: cellToBase64(externalMessage),
    externalMessageHashHex: bytesToHex(externalMessage.hash()),
    // The SAME hash bytes in the encoding the confirmation lane speaks:
    // the relay answers tx_hash as base64 of this cell hash and the wallet
    // indexer publishes in_msg_hash in base64 too, and the match is a strict
    // string equality. Any path that has to name the external locally (a
    // node refusal answered before the relay could) must use this form or
    // its inclusion push can never bind.
    externalMessageHashB64: bytesToBase64(externalMessage.hash()),
    includesStateInit: includeStateInit,
    messageCount: messages.length,
  }
}

function createTonV5R1Wallet(publicKey: Uint8Array, network: TonNetwork): WalletContractV5R1 {
  ensureBufferGlobal()
  assertByteLength(publicKey, 32, 'ed25519 public key')
  return WalletContractV5R1.create({
    publicKey: toBuffer(publicKey),
    walletId: {
      networkGlobalId: networkGlobalId(network),
      context: {
        workchain: DEFAULT_WORKCHAIN,
        walletVersion: WALLET_VERSION,
        subwalletNumber: DEFAULT_SUBWALLET_NUMBER,
      },
    },
  })
}

function networkGlobalId(network: TonNetwork): number {
  return network === 'testnet' ? TESTNET_GLOBAL_ID : MAINNET_GLOBAL_ID
}

function ensureBufferGlobal(): void {
  const globalWithBuffer = globalThis as typeof globalThis & {
    Buffer?: typeof Buffer
  }
  if (!globalWithBuffer.Buffer) {
    Object.defineProperty(globalThis, 'Buffer', {
      configurable: true,
      value: Buffer,
      writable: true,
    })
  }
}

function cellToBase64(cell: Cell): string {
  return cell.toBoc({ idx: false }).toString('base64')
}

function toRelaxedMessage(message: TonV5R1InternalMessagePlan): MessageRelaxed {
  if (message.amountNano <= 0n) {
    throw new Error('message amountNano must be positive')
  }
  return internal({
    to: message.to,
    value: message.amountNano,
    bounce: message.bounce ?? true,
    body: message.bodyBocBase64 ? Cell.fromBase64(message.bodyBocBase64) : undefined,
  })
}

function toBuffer(bytes: Uint8Array): Buffer {
  return Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

function copyBytes(bytes: Uint8Array): Uint8Array {
  return new Uint8Array(bytes)
}

function assertByteLength(bytes: Uint8Array, expected: number, label: string): void {
  if (bytes.byteLength !== expected) {
    throw new Error(`${label} must be ${expected} bytes`)
  }
}

function assertInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer`)
  }
}

function assertMessages(messages: TonV5R1InternalMessagePlan[]): void {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('at least one outgoing message is required')
  }
  if (messages.length > MAX_V5R1_OUT_MESSAGES) {
    throw new Error(`v5r1 transfer supports at most ${MAX_V5R1_OUT_MESSAGES} messages`)
  }
}

function assertValidUntilFresh(
  validUntil: number,
  nowUnix = Math.floor(Date.now() / 1000),
  minValidUntilSeconds = DEFAULT_MIN_VALID_UNTIL_SECONDS
): void {
  assertInteger(nowUnix, 'nowUnix')
  assertInteger(minValidUntilSeconds, 'minValidUntilSeconds')
  if (minValidUntilSeconds <= 0) {
    throw new Error('minValidUntilSeconds must be positive')
  }
  if (validUntil <= nowUnix + minValidUntilSeconds) {
    throw new Error('validUntil is expired or too close to expiry')
  }
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) {
    return false
  }
  let diff = 0
  for (let index = 0; index < left.byteLength; index += 1) {
    diff |= (left[index] ?? 0) ^ (right[index] ?? 0)
  }
  return diff === 0
}

function bytesToBase64(bytes: Uint8Array): string {
  return Buffer.from(bytes).toString('base64')
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
